import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

export type OrderStatusPreviewShop = {
  id: string;
  name: string;
  phone?: string | null;
  contact_email?: string | null;
  link_instagram?: string | null;
  link_facebook?: string | null;
};

export type OrderStatusPreviewOrder = {
  id: string;
  number: string;
  status:
    "PAYMENT" | "WAITING" | "PREPARATION" | "READY" | "COLLECTED" | "CANCELLED";
  type: "delivery" | "click-and-collect" | "on-site" | "eat-in";
  ready_at: string | null;
  created_at: string;
  estimated_time_of_arrival?: string | null;
  cancellation_reason?: string | null;
  multi_shop?: boolean;
  onsite_location_id?: string | null;
  shop: OrderStatusPreviewShop | null;
  pickup_location?: {
    street_line1: string;
    post_code: string;
    city: string;
    street_line2?: string;
    point?: { coordinates: number[] } | null;
  } | null;
  delivery_address?: {
    addressee?: string;
    post_code?: string;
    city?: string;
  } | null;
};

/**
 * Serializable preview VM for `OrderStatusBlockComponent`.
 * Kept in content-builder so dashboard + webapp share the same transport shape.
 */
export type OrderStatusBlockScenarioContent = {
  orders: readonly OrderStatusPreviewOrder[];
  onSiteLocationName: string;
};

export const ORDERSTATUSBLOCK_LEGACY_FIELD_ORDER = [
  "display_shop_contact",
  "display_localize_button",
  "display_details_button",
  "display_order_status",
  { type: "separator" },
  { type: "heading", value: "Couleurs" },
  "text_color",
  "secondary_text_color",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  orderStatusBlockDescriptor: _generatedOrderStatusBlockDescriptor,
  orderStatusBlockBlocDefinition,
  orderStatusBlockDashboardLayout,
  orderStatusBlockConfig,
  orderStatusBlockResolveConfig,
  orderStatusBlockProperties,
  orderStatusBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Order list",
    componentType: "OrderStatusBlockComponent",
    category: "content",
    description: "Order list.",
    keywords: [],
    allowedIn: [{ bloc: "OrderStatusPageComponent", slot: "*" }],

    sharedFields: {},
  },

  () => ({
    display_shop_contact: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.order-status.display-shop-contact.label",
      },
    }),
    display_localize_button: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.order-status.display-localize-button.label",
      },
    }),
    display_details_button: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.order-status.display-details-button.label",
      },
    }),
    display_order_status: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.order-status.display-order-status.label",
      },
    }),
    on_site_time_source: propertyValue({
      type: "select",
      value: "created_at",
      options: {
        label: "pages.blocks.order-status.on-site-time-source.label",
        help: "pages.blocks.order-status.on-site-time-source.help",
        choices: [
          {
            value: "created_at",
            label: "pages.blocks.order-status.on-site-time-source.options.created-at",
          },
          {
            value: "ready_at",
            label: "pages.blocks.order-status.on-site-time-source.options.ready-at",
          },
        ],
      },
    }),
    on_site_created_at_label: propertyValue({
      type: "translation",
      value: {
        fr: "Créée",
        en: "Order created at",
        es: "Creado",
        nl: "Gemaakt op",
        de: "Erstellt",
        it: "Creato",
        hu: "Létrehozva",
      },
      options: {
        label: "pages.blocks.order-status.on-site-created-at-label.label",
        help: "pages.blocks.order-status.on-site-created-at-label.help",
      },
    }),
    on_site_ready_at_label: propertyValue({
      type: "translation",
      value: {
        fr: "Heure de retrait",
        en: "Pickup time",
        es: "Hora de recogida",
        nl: "Ophaaltijd",
        de: "Abholzeit",
        it: "Orario di ritiro",
        hu: "Átvételi időpont",
      },
      options: {
        label: "pages.blocks.order-status.on-site-ready-at-label.label",
        help: "pages.blocks.order-status.on-site-ready-at-label.help",
      },
    }),
    display_custom_button: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.order-status.display-custom-button.label",
      },
    }),
    custom_button_text: propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.order-status.custom-button-text.label",
      },
    }),
    custom_button_url: propertyValue({
      type: "text",
      value: "",
      options: {
        label: "pages.blocks.order-status.custom-button-url.label",
      },
    }),
    text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: {
        label: "pages.blocks.order-status.text-color.label",
      },
    }),
    secondary_text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: {
        label: "pages.blocks.order-status.secondary-text-color.label",
      },
    }),
  }),

  () => ({
    omitFields: [
      "display_custom_button",
      "custom_button_text",
      "custom_button_url",
    ] as const,
    tabs: [
      {
        label: "pages.blocks.order-status.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.order-status.fieldset.order-card.label",
            fields: [
              "display_order_status",
              "on_site_time_source",
              {
                prop: "on_site_created_at_label",
                visibleWhen: { on_site_time_source: "created_at" },
              },
              {
                prop: "on_site_ready_at_label",
                visibleWhen: { on_site_time_source: "ready_at" },
              },
              "display_localize_button",
              "display_details_button",
              "text_color",
              "secondary_text_color",
            ],
          },
          {
            legend: "pages.blocks.order-status.fieldset.shop-contact.label",
            fields: ["display_shop_contact"],
          },
        ],
      },
    ],
  }),
);

export function orderStatusBlockDescriptor(): IBlockDescriptor;
export function orderStatusBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function orderStatusBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedOrderStatusBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedOrderStatusBlockDescriptor,
    orderStatusBlockProperties,
    fieldOrder,
  );
}

export {
  orderStatusBlockBlocDefinition,
  orderStatusBlockDashboardLayout,
  orderStatusBlockProperties,
  orderStatusBlockConfig,
  orderStatusBlockResolveConfig,
  orderStatusBlockDefinitionSetupScenarios,
};

export type OrderStatusBlockConfig = typeof orderStatusBlockConfig;
